import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';

const SetPinDialog = ({ open, onOpenChange, onSubmit, loading = false, isUpdate = false }) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const confirmRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (open) {
      setPin(['', '', '', '']);
      setConfirmPin(['', '', '', '']);
      setStep(1);
      setPassword('');
      setError('');
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (refs, state, setState, index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newArr = [...state];
    newArr[index] = value.slice(-1);
    setState(newArr);
    setError('');
    if (value && index < 3) {
      refs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (refs, state, index, e) => {
    if (e.key === 'Backspace' && !state[index] && index > 0) {
      refs[index - 1].current?.focus();
    }
  };

  const handleNext = () => {
    if (pin.join('').length !== 4) {
      setError('Please enter all 4 digits');
      return;
    }
    setStep(2);
    setTimeout(() => confirmRefs[0].current?.focus(), 100);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fullPin = pin.join('');
    const fullConfirm = confirmPin.join('');
    if (fullConfirm.length !== 4) {
      setError('Please enter all 4 digits');
      return;
    }
    if (fullPin !== fullConfirm) {
      setError('PINs do not match. Try again.');
      setConfirmPin(['', '', '', '']);
      setTimeout(() => confirmRefs[0].current?.focus(), 100);
      return;
    }
    if (!password.trim()) {
      setError('Enter your account password');
      return;
    }
    onSubmit(fullPin, password);
  };

  const renderPinInputs = (refs, state, setState, stepNum) => (
    <div className="flex justify-center gap-3">
      {state.map((digit, idx) => (
        <Input
          key={`setpin-${stepNum}-${idx}`}
          ref={refs[idx]}
          data-testid={`set-pin-input-${stepNum}-${idx}`}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(refs, state, setState, idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(refs, state, idx, e)}
          className="w-14 h-14 text-center text-2xl font-bold"
        />
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle data-testid="set-pin-dialog-title">{isUpdate ? 'Change' : 'Set'} Delete PIN</DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            {step === 1 ? 'Enter a 4-digit PIN for delete operations.' : 'Confirm your PIN.'}
          </p>
        </DialogHeader>
        <form onSubmit={step === 2 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }} className="space-y-4">
          {step === 1 ? renderPinInputs(inputRefs, pin, setPin, 1) : renderPinInputs(confirmRefs, confirmPin, setConfirmPin, 2)}
          {step === 2 && (
            <Input
              data-testid="set-pin-password-input"
              type="password"
              autoComplete="current-password"
              placeholder="Your account password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
            />
          )}
          {error && <p className="text-red-500 text-sm text-center" data-testid="set-pin-error">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {step === 1 ? (
              <Button data-testid="set-pin-next-btn" type="submit">Next</Button>
            ) : (
              <Button data-testid="set-pin-confirm-btn" type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Confirm PIN'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SetPinDialog;
